// THE CATEGORY LAYER of the Email Workspace (2026-09-15 parity audit, F1-1 / F2-3, bff#38; frames
// 40006970:297318, 40008255:426104, 40008287:152790, 40008242:423135, 40008398:181751).
//
// "14 email groups" is no longer one flat listing: the strip under the crumb lists the CATEGORIES
// the sender groups fall in — Social media · Shopping · Work & Tools · Finance · Health · Small
// groups · Other — each with its group count and a "View ↗" that narrows the middle to that
// category's groups ("Social media groups", with "Back" beside the input and the rail retitled
// "Social media timeline"). After an archive the strip re-counts, and the rail's receipt names the
// category the archived groups came from. The create-flow frame adds a chooser AT REST built from
// the same answer: "Create ongoing Health groups 762 emails (Recommended) / Archive work & tools
// groups 49 emails / Clean-up 6 groups under 5 emails".
//
// The categorisation is the BFF's (`POST v1/email/groups/summary`): each group row carries
// `category`, and the summary lists the categories in strip order with their senders, plus the
// suggestions with their senders pre-resolved. This module is what the web does with that: the
// rows, the sub-listing filter, the crumb / title / rail wording, and the suggestion rows — each
// suggestion PRE-TICKS its senders and arms the flow that already exists (archive, create), because
// the BFF adds no new write endpoint for them.
//
// Pure — no store, no api, nothing mounted.

import { emailsLabel } from '@/lib/emailSession'

export const CATEGORY_TEXT = {
  /** "Small groups" is the one category whose name already says "groups". */
  small: 'Small groups',
  back: 'Back',
}

/**
 * What the strip CALLS a category (frame 40008241:418476, Pouria 2026-09-15 local batch): the
 * server's own name, except "Social media", which the frame writes as "Social media groups" —
 * "Shopping", "Work & Tools", "Finance", "Other" and "Small groups" stand as they are. The
 * category KEY (`c.category`) is untouched: it is what opens the listing and what the server
 * answers to.
 */
export const STRIP_NAME = { 'Social media': 'Social media groups' }
export const stripName = (category) => STRIP_NAME[category] || category

/** The request body `POST v1/email/groups/summary` takes — the same one `email/groups` takes. */
export function summaryRequest({ minCount = 2, take = 50 } = {}) {
  return { minCount, take }
}

/** The summary an answer carries, junk dropped. */
export function readSummary(payload) {
  const body = payload?.data?.data ?? payload?.data ?? payload ?? {}
  return {
    smallGroupMax: Number(body?.smallGroupMax) || 5,
    categories: (Array.isArray(body?.categories) ? body.categories : [])
      .filter(c => c && c.category)
      .map(c => ({
        category: String(c.category),
        groupCount: Number(c.groupCount) || 0,
        emailCount: Number(c.emailCount) || 0,
        unseenCount: Number(c.unseenCount) || 0,
        senders: (c.senders || []).filter(Boolean).map(String),
      })),
    suggestions: (Array.isArray(body?.suggestions) ? body.suggestions : [])
      .filter(s => s && s.kind && s.label)
      .map(s => ({
        kind: String(s.kind),
        category: String(s.category || ''),
        label: String(s.label),
        groupCount: Number(s.groupCount) || 0,
        emailCount: Number(s.emailCount) || 0,
        senders: (s.senders || []).filter(Boolean).map(String),
        recommended: !!s.recommended,
      })),
  }
}

/** "Social media groups" — the middle's title for a category sub-listing (40008255:426104). */
export function categoryTitle(category) {
  const name = String(category || '').trim()
  if (!name) return ''
  return /groups$/i.test(name) ? name : `${name} groups`
}

/** "3 Social media groups" — the crumb for the sub-listing (frame 40008287:152790). */
export function categoryCrumb(category, groupCount) {
  const title = categoryTitle(category)
  return title ? `${Number(groupCount) || 0} ${title}` : ''
}

/** "Social media timeline" — the rail's header while the sub-listing is the middle. */
export function categoryTimelineTitle(category) {
  const name = String(category || '').trim()
  return name ? `${name} timeline` : ''
}

/** "Social media email groups" — what the archive receipt names (frame 40008242:423135). */
export function categoryReceiptName(category) {
  const name = String(category || '').trim()
  if (!name) return ''
  return /groups$/i.test(name) ? name.replace(/groups$/i, 'email groups') : `${name} email groups`
}

/**
 * The strip's category rows (frames 40006970:297318 / 40008398:181751): the category's own name,
 * its group count and a "View ↗" — in the server's strip order, and only the categories that have
 * groups (the server omits zero headings; a re-count after an archive drops the emptied one).
 */
export function categoryRows(categories) {
  return (categories || [])
    .filter(c => c && c.category && (Number(c.groupCount) || 0) > 0)
    .map(c => ({
      id: `category:${c.category}`,
      category: c.category,
      name: stripName(c.category),
      count: Number(c.groupCount) || 0,
      emails: Number(c.emailCount) || 0,
      senders: c.senders || [],
    }))
}

/**
 * The groups that belong to a category — by the summary's own sender list, falling back to the
 * `category` each group row carries, so a category opened before the summary landed still lists.
 */
export function groupsInCategory(groups, category, categories = []) {
  const name = String(category || '').trim()
  if (!name) return groups || []
  const entry = (categories || []).find(c => c && c.category === name)
  const senders = new Set((entry?.senders || []).map(s => String(s).toLowerCase()))
  return (groups || []).filter(g => g && (
    senders.has(String(g.sender || '').toLowerCase()) || String(g.category || '') === name
  ))
}

/**
 * The ONE category a set of senders all share, or '' when they span several (or none is known).
 * What the archive receipt names: "Social media email groups" is only true of an archive that
 * emptied social media and nothing else.
 */
export function categoryOfSenders(senders, groups, categories = []) {
  const picked = new Set((senders || []).map(s => String(s).toLowerCase()))
  const found = new Set()
  for (const g of groups || []) {
    if (!g || !picked.has(String(g.sender || '').toLowerCase())) continue
    const own = String(g.category || '').trim()
    const fromSummary = (categories || []).find(c => (c?.senders || []).some(s => String(s).toLowerCase() === String(g.sender || '').toLowerCase()))
    const name = own || fromSummary?.category || ''
    if (name) found.add(name)
    else return ''
  }
  return found.size === 1 ? [...found][0] : ''
}

/** Which armed option a category suggestion stands for — the flows that already exist. */
const SUGGESTION_ARMS = {
  'create-ongoing': 'create-ongoing',
  archive: 'archive-selected',
  'clean-up': 'archive-selected',
}

/**
 * The at-rest chooser's rows (frame 40008398:181751): the server's label plus the web's count —
 * "Create ongoing Health groups 762 emails" — Recommended where the server says so, each carrying
 * the senders it pre-ticks and the option it arms.
 */
export function categorySuggestionRows(suggestions) {
  return (suggestions || [])
    .filter(s => s && s.label && SUGGESTION_ARMS[s.kind] && (s.senders || []).length)
    .map(s => ({
      id: `category-${s.kind}:${s.category || 'all'}`,
      kind: s.kind,
      category: s.category,
      // The clean-up label already carries its own count ("Clean-up 6 groups under 5 emails").
      text: s.kind === 'clean-up' ? s.label : `${s.label} ${emailsLabel(s.emailCount)}`,
      recommended: !!s.recommended,
      ready: true,
      senders: s.senders,
      arms: SUGGESTION_ARMS[s.kind],
    }))
}

export default categoryRows
